import React from 'react';
import {
  Sparkles,
  ClipboardCheck,
  ShieldCheck,
  BarChart3,
  LineChart,
  Layers,
  Landmark,
  Users,
  Cpu,
  Database,
  Clock,
  Wrench,
  CheckCircle2,
  ArrowRight,
  Mail,
  MousePointerClick,
  GraduationCap,
  UserCheck,
  BookOpen,
  LifeBuoy,
  MapPin,
  Gauge,
  Banknote,
  Server,
  Receipt,
  Info,
  ChevronDown,
} from 'lucide-react';

import { Brand } from '../../components/EngageDeck';
import { Choice, ResponsesSummary, useSelectable, useAnswer, FocusChoice, Segmented, LogoSlot } from '../../components/Capture';
import DataFlow from '../../components/DataFlow';
import Advice from '../../components/Advice';
import adviceSlides from './adviceSlides';
import CloseTimeline, { ClosePeriodProvider, CloseActions } from './CloseTimeline';
import logos from './logos';

/* ======================================================
   NobleOak — engagement proposal slides
   Each slide: { id, variant?: 'alt' | 'center', hero?, footnote?, content }
   Clickable questions live in ./questions.js
====================================================== */

const CONTACT = {
  name: 'Joshua C.',
  title: 'Engagement Director',
  email: 'team@optiii.com',
};

const Check = ({ children }) => (
  <div className="check">
    <CheckCircle2 size={26} />
    <span>{children}</span>
  </div>
);

const Prompt = ({ children = 'Click to select' }) => (
  <span className="prompt">
    <MousePointerClick size={22} />
    {children}
  </span>
);



/* How we connect each accounting system (typical routes; confirmed in the diagnostic) */
const LEDGER_NOTES = {
  Workday: 'Workday web services and custom reports (RaaS) for balances and journals; journals loaded back through Workday’s integration tools.',
  NetSuite: 'SuiteTalk web services and SuiteQL for balances and transactions; existing saved searches reused; journals posted back through the API.',
  MYOB: 'MYOB’s API for transactions and balances; journals posted back through the API.',
  Xero: 'Xero’s Accounting API for transactions, bank statement lines and journals; native bank feeds already in place.',
  SAP: 'OData services or scheduled extracts from S/4HANA; journals through standard posting interfaces.',
  Oracle: 'Oracle Fusion REST APIs and BI Publisher extracts; journals through the standard GL import.',
  'Microsoft Dynamics 365': 'Dataverse / OData data entities for balances and journals.',
  TechnologyOne: 'TechnologyOne integration APIs or scheduled extracts; journals through standard import templates.',
  'Sage Intacct': 'Sage Intacct Web Services API for transactions, balances and journals.',
  Other: 'We start from what your system exposes — an API, read access to its database, or scheduled file exports.',
};

const LAYER = {
  'Recommended third-party tools': {
    text: 'Proven products, selected and configured for you',
    chips: ['Integration: Workato, Boomi', 'Recs & close: BlackLine, FloQast', 'Reporting: Power BI'],
  },
  'Build internal tools with your team': {
    text: 'Built with your team, owned by NobleOak',
    chips: ['Warehouse: Snowflake, Azure SQL', 'Python / SQL pipelines', 'Internal recs & reporting app'],
  },
  'A mix — advise us': {
    text: 'Buy where products are mature, build where you’re unique',
    chips: ['Integration platform', 'Shared policy data layer', 'Internal insurer-specific recs'],
  },
};

const LedgerFlow = ({ ledger }) => {
  const { selected } = useAnswer('approach');
  const approach = selected[0] || 'A mix — advise us';
  const layer = LAYER[approach];
  return (
    <>
      <DataFlow
        sources={[
          { icon: Server, title: 'Operating data', text: 'Policy admin, partner and reinsurer files' },
          { icon: Banknote, title: 'Bank feeds & statements', text: 'Daily transactions and statements' },
          { icon: BookOpen, title: ledger === 'Other' ? 'Your accounting system' : ledger, text: 'GL, sub-ledgers and journals', highlight: true },
        ]}
        layer={{ title: 'Integration & controls layer', text: layer.text, chips: layer.chips }}
        outputs={[
          { icon: Receipt, title: 'Automated reconciliations', text: 'Bank, premium and partner; exceptions only' },
          { icon: ClipboardCheck, title: 'Journals & close', text: 'Posted back to the ledger, with approvals' },
          { icon: BarChart3, title: 'Reporting', text: 'Board, investor and APRA, traced to source' },
        ]}
      />
      <div className="notes">
        <div className="noteCard"><div className="dayLabel">Connecting {ledger === 'Other' ? 'your ledger' : ledger}</div><p>{LEDGER_NOTES[ledger]}</p></div>
        <div className="noteCard"><div className="dayLabel">Bank feeds</div><p>Daily statement files from your banks (or an aggregator), matched automatically against the ledger and premium receipts.</p></div>
        <div className="noteCard"><div className="dayLabel">Operating data</div><p>Policy and partner data lands in one governed layer, mapped to your chart of accounts so every number traces back.</p></div>
      </div>
    </>
  );
};


const WORKSPACE_NOTES = {
  'Microsoft 365': [
    'Workflow files and approvals live in SharePoint and Teams',
    'Power Automate for hand-offs; Power BI for reporting',
    'Access through your existing Microsoft sign-in',
  ],
  'Google Workspace': [
    'Workflow files and approvals live in Google Drive',
    'Sheets and Apps Script for light automation; Looker Studio for reporting',
    'Access through your existing Google sign-in',
  ],
  Both: [
    'We pick one home for finance workflows and connect the other',
    'Files and approvals don’t split across two places',
    'Access follows your main identity provider',
  ],
  Other: [
    'We work with the tools you use for files, sign-in and approvals',
    'Nothing is moved outside your environment',
    'Access follows your existing identity provider',
  ],
};


/* ---------- Engagement timeline ---------- */
const MONTHS = 4.5; // axis length; the retainer runs on past 3.5
const STAGES = [
  {
    key: 's1', icon: Clock, title: 'Diagnostic & opportunity map', when: 'Weeks 1–2 · done by 0.5 months', from: 0, to: 0.5,
    text: 'Map close, reporting, data sources and controls; a sized opportunity register and quick wins.',
  },
  {
    key: 's2', icon: ClipboardCheck, title: 'Blueprint & delivery plan', when: 'Done by 1.5 months', from: 0.5, to: 1.5,
    text: 'Detailed architecture, internal build vs vendor selection, governance and owners, and the delivery backlog.',
  },
  {
    key: 's3', icon: Wrench, title: 'Implementation', when: 'Live and in testing by 3.5 months', from: 1.5, to: 3.5, milestone: 'Live & in testing',
    text: 'Automations built in sprints with your team, tested against real month-ends, documented and handed over.',
  },
  {
    key: 's4', icon: LifeBuoy, title: 'Ongoing retainer', when: 'From 3.5 months · optional', from: 3.5, to: 4.5, open: true,
    text: 'Monitoring, new workflows and support through future closes and reporting cycles.',
  },
];
const pos = (m) => `${(m / MONTHS) * 100}%`;

const EngagementTimeline = () => (
  <div className="gantt">
    <div className="ganttRow ganttAxisRow">
      <div />
      <div className="ganttAxis">
        {[0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5].map((m) => (
          <span key={m} className={[0.5, 1.5, 3.5].includes(m) ? 'key' : ''} style={{ left: pos(m) }}>
            {m === 0 ? 'Start' : `${m} mo`}
          </span>
        ))}
      </div>
    </div>
    {STAGES.map((st, i) => (
      <div className="ganttRow" key={st.key}>
        <div className="ganttLabel">
          <div className="iconWrap" style={{ width: 60, height: 60, borderRadius: 18 }}><st.icon size={28} /></div>
          <div>
            <div className="ganttTitle"><span className="ganttNum">{i + 1}</span>{st.title}</div>
            <div className="ganttWhen">{st.when}</div>
            <div className="ganttText">{st.text}</div>
          </div>
        </div>
        <div className="ganttTrack">
          <div className="ganttGrid" />
          <div className={`ganttBar ${st.key} ${st.open ? 'open' : ''}`} style={{ left: pos(st.from), width: pos(st.to - st.from) }} />
          {st.milestone && (
            <div className="ganttMilestone" style={{ left: pos(st.to) }}>
              <span className="diamond" />
              <span className="mLabel">{st.milestone}</span>
            </div>
          )}
        </div>
      </div>
    ))}
    <div className="ganttRow ganttTraining">
      <div className="ganttLabel">
        <div className="iconWrap" style={{ width: 60, height: 60, borderRadius: 18 }}><GraduationCap size={28} /></div>
        <div>
          <div className="ganttTitle">On-the-ground training</div>
          <div className="ganttWhen">Throughout — your team builds alongside us</div>
        </div>
      </div>
      <div className="ganttTrack">
        <div className="ganttGrid" />
        <div className="ganttBar training" style={{ left: 0, width: pos(3.5) }} />
        <div className="ganttBar training open" style={{ left: pos(3.5), width: pos(1) }} />
      </div>
    </div>
  </div>
);


/* ---------- Personalised plan (uses the chosen start month) ---------- */
const START_DATES = {
  'November 2026': '2026-11-02',
  'Early December 2026': '2026-12-01',
  'January 2027': '2027-01-11',
  'February 2027 or later': '2027-02-01',
};
const NOBLEOAK_DATES = [
  { date: '2026-12-31', label: '1H FY27 half-year close' },
  { date: '2027-06-30', label: 'FY27 year end' },
  { date: '2027-12-01', label: 'Life Company transition', monthOnly: true },
];
const addD = (iso, days) => new Date(new Date(`${iso}T00:00:00Z`).getTime() + days * 86400000);
const fmtD = (date, monthOnly) =>
  date.toLocaleDateString('en-AU', monthOnly ? { month: 'short', year: 'numeric', timeZone: 'UTC' } : { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

const YourPlan = () => {
  const { selected } = useAnswer('start');
  const start = selected[0] && START_DATES[selected[0]] ? selected[0] : 'November 2026';
  const s0 = START_DATES[start];
  const ours = [
    { date: addD(s0, 0), label: 'Diagnostic starts', tag: 'fixed' },
    { date: addD(s0, 11), label: 'Diagnostic readout to the CFO', tag: 'fixed' },
    { date: addD(s0, 45), label: 'Blueprint & delivery plan ready', tag: 'quoted' },
    { date: addD(s0, 106), label: 'Automations live & in testing', tag: 'quoted' },
  ].map((x) => ({ ...x, ours: true }));
  const theirs = NOBLEOAK_DATES.map((x) => ({ date: new Date(`${x.date}T00:00:00Z`), label: x.label, monthOnly: x.monthOnly }));
  const rows = [...ours, ...theirs].sort((a, b) => a.date - b.date);
  return (
    <div>
      <div className="focusLabel" style={{ marginBottom: 10 }}>
        Your plan · {selected[0] ? `${start} start` : 'assuming a November start — pick yours on the investment slide'}
      </div>
      <div className="plan">
        {rows.map((r) => (
          <div key={r.label} className={`planRow ${r.ours ? 'ours' : 'theirs'}${r.tag === 'quoted' ? ' later' : ''}`}>
            <span className="planDate">{fmtD(r.date, r.monthOnly)}</span>
            <span className="planWhat">
              <span className="planDot" />{r.label}
              {r.tag && <span className={`planTag ${r.tag}`}>{r.tag === 'fixed' ? 'Diagnostic' : 'Quoted after diagnostic'}</span>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};


/* Agenda: matches the section names shown in each slide's footer */
const AGENDA = [
  ['Where you are', 'NobleOak’s finance team today, and the mission for $1bn', 4],
  ['Getting it right', 'Why AI is harder in finance, what to build vs buy, and what APRA expects', 8],
  ['The deadline', 'What finance needs in place before the Dec 2027 Life Company transition', 3],
  ['Why Optiii', 'What we do, and the team that builds with you and trains your people', 4],
  ['How we’d work together', 'From diagnostic to live in 3.5 months', 3],
  ['First step', 'The two-week diagnostic and your plan', 3],
  ['Discussion', 'Your questions and priorities', 5],
];


/* ---------- Finance today: gaps + stage ---------- */
const GAPS = [
  ['Close', 'Close lands after BD4', 'Month-end finishes mid-month, squeezing audit, board and investor time'],
  ['Spreadsheets', 'Excel holds the close together', 'Key workbooks with logic only one or two people fully understand'],
  ['Data', 'Manual extracts from the ledger', 'GL data pulled out and re-keyed into models and packs every month'],
  ['Regulatory', 'APRA returns built by hand', 'Capital and LRS returns assembled in spreadsheets with thin lineage'],
  ['Partners', 'Partner data arrives in different shapes', 'nib, NEOS, Budget Direct and RAC each send premium and commission data differently'],
  ['Reconciliations', 'Premium & reinsurance recs are manual', 'Matching done at month-end rather than daily, with exceptions found late'],
  ['Actuarial', 'Actuarial–finance hand-offs', 'AASB 17 and Embedded Value numbers move between teams by file'],
  ['Reporting', 'Underlying vs statutory bridges', 'Adjustments such as the Victorian stamp duty provision re-explained each period'],
  ['Board', 'Board pack takes days', 'Tables and commentary assembled by hand for every board and results cycle'],
  ['People', 'Knowledge sits with a few people', 'Close and regulatory processes depend on individuals, not documented workflows'],
];

const FINANCE_STAGES = [
  ['Foundational', 'Spreadsheet-led, manual close, little documentation'],
  ['Scaling', 'Listed and growing fast; processes catching up with scale'],
  ['Systemised', 'BD4–5 close, governed data, automated reconciliations'],
  ['Insight-led', 'Near real-time reporting, AI-assisted analysis'],
];
const OUR_READ = 'Scaling';

const FinanceStage = () => {
  const { selected, toggle } = useAnswer('financeStage');
  const chosen = selected[0];
  return (
    <div>
      <div className="choiceQ" style={{ marginBottom: 12 }}>
        <span>Stage of the finance team</span>
        <span className="choiceHint">Click to correct</span>
      </div>
      <div className="stages">
        {FINANCE_STAGES.map(([name, d]) => {
          const isChosen = chosen ? chosen === name : name === OUR_READ;
          return (
            <button type="button" key={name} className={`stage ${isChosen ? 'on' : ''}`} onClick={() => toggle(name)} aria-pressed={chosen === name}>
              <span className="stageName">
                {name}
                {!chosen && name === OUR_READ && <em>Our read</em>}
                {chosen === name && <em>Your view</em>}
              </span>
              <span className="stageDesc">{d}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};


/* ---------- Four main pain points, each expanding to the detailed gaps ---------- */
const THEMES = [
  {
    icon: Clock,
    title: 'A slow, manual close',
    gaps: ['Close lands after BD4', 'Excel holds the close together', 'Premium & reinsurance recs are manual'],
    cost: 'Audit, board and investor work squeezed into the days that are left',
    good: 'BD4 close with daily reconciliations and exception-only review',
  },
  {
    icon: Database,
    title: 'Data that doesn’t connect',
    gaps: ['Manual extracts from the ledger', 'Partner data arrives in different shapes', 'Actuarial–finance hand-offs'],
    cost: 'Hours re-keying and re-checking, and numbers that differ by source',
    good: 'One governed data layer feeding every report and reconciliation',
  },
  {
    icon: Landmark,
    title: 'Regulatory & board reporting by hand',
    gaps: ['APRA returns built by hand', 'Underlying vs statutory bridges', 'Board pack takes days'],
    cost: 'Late nights before every return and results cycle, with lineage that’s hard to evidence',
    good: 'Packs and returns drafted automatically, traceable to source, reviewed by people',
  },
  {
    icon: Users,
    title: 'Key-person risk',
    gaps: ['Knowledge sits with a few people'],
    cost: 'Leave or departures put close and regulatory deadlines at risk',
    good: 'Documented, owned workflows any trained team member can run',
  },
];





const GapRow = ({ title }) => {
  const { on, props } = useSelectable('notGaps', title);
  const detail = GAPS.find((g) => g[1] === title)?.[2];
  return (
    <div className={`gapRow selectable ${on ? 'off' : ''}`} {...props}>
      <div>
        <b>{title}</b>
        <span>{detail}</span>
      </div>
      <span className="gapState">{on ? 'Not a gap' : 'Likely gap'}</span>
    </div>
  );
};

const PainThemes = () => {
  const ruledOut = useAnswer('notGaps').selected;
  const [open, setOpen] = React.useState(null);
  return (
    <div className={`painList ${open !== null ? 'hasOpen' : ''}`}>
      {THEMES.map((t, i) => {
        const live = t.gaps.filter((g) => !ruledOut.includes(g));
        const off = live.length === 0;
        const isOpen = open === i;
        return (
          <div key={t.title} className={`painTheme ${isOpen ? 'open' : ''} ${off ? 'off' : ''}`}>
            <button type="button" className="painHead" onClick={() => setOpen(isOpen ? null : i)} aria-expanded={isOpen}>
              <div className="iconWrap" style={{ width: 48, height: 48, borderRadius: 14 }}><t.icon size={24} /></div>
              <div className="painHeadText">
                <h3>{t.title}</h3>
                <span>{t.cost}</span>
              </div>
              <span className={`pill ${off ? '' : 'next'}`}>{off ? 'Ruled out' : `${live.length} of ${t.gaps.length} likely`}</span>
              <ChevronDown size={24} className="painChevron" />
            </button>
            {isOpen && (
              <div className="painBody">
                {t.gaps.map((g) => <GapRow key={g} title={g} />)}
                <div className="painRow good"><span>What good looks like</span>{t.good}</div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ---------- Live data map: fills in from answers given across the deck ---------- */
const DataMap = () => {
  const policy = useAnswer('policyData').selected;
  const ledger = useAnswer('ledger').selected;
  const reporting = useAnswer('reporting').selected;
  const workspace = useAnswer('workspace').selected;
  const owner = useAnswer('dataTeam').selected;
  const team = useAnswer('teamSize').selected;
  const stages = [
    { label: 'Sources', icon: Server, rows: [['Policy & premium', policy], ['Bank feeds', ['Daily statements']]] },
    { label: 'Ledger', icon: BookOpen, rows: [['Accounting system', ledger]] },
    { label: 'Reporting', icon: BarChart3, rows: [['Reporting & planning', reporting], ['Workplace', workspace]] },
  ];
  const known = [policy, ledger, reporting, workspace, owner, team].filter((x) => x.length).length;
  return (
    <div className="card dataMap">
      <div className="dmHead">
        <b>NobleOak data map</b>
        <span>{known} of 6 known</span>
      </div>
      {stages.map((st, idx) => (
        <React.Fragment key={st.label}>
          <div className="dmStage">
            <div className="dmLabel"><st.icon size={20} />{st.label}</div>
            <div className="dmRows">
              {st.rows.map(([k, v]) => (
                <div className="dmRow" key={k}>
                  <span>{k}</span>
                  {v.length ? (
                    <span className="dmChips">{v.map((x) => <span className="dmChip" key={x}>{x}</span>)}</span>
                  ) : (
                    <span className="dmEmpty">To confirm</span>
                  )}
                </div>
              ))}
            </div>
          </div>
          {idx < stages.length - 1 && <div className="dmArrow" aria-hidden="true">↓</div>}
        </React.Fragment>
      ))}
      <div className="dmFoot">
        <span>Data owner: <b>{owner[0] || 'to confirm'}</b></span>
        <span>Finance team: <b>{team[0] || 'to confirm'}</b></span>
      </div>
    </div>
  );
};

const slides = [
  /* 1 — Cover */
  {
    id: 'cover',
    variant: 'center',
    hero: true,
    content: (
      <>
        <div className="topbar">
          <Brand size={30} />
          <div className="tag" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}><LogoSlot name="NobleOak" logos={logos} size={22} showName={false} />Prepared for NobleOak Life Limited · October 2026</div>
        </div>
        <div style={{ maxWidth: 1560 }}>
          <div className="eyebrow">
            <Sparkles size={24} />
            Specialists in finance uplift &amp; automation
          </div>
          <h1>
            Helping NobleOak build a finance function that scales with <em>accuracy</em>, <em>control</em>, and{' '}
            <em>leverage</em>
          </h1>
          <p className="sub" style={{ margin: '36px 0 44px', fontSize: 30, maxWidth: 1300 }}>
            Practical automation and bounded AI across close, reporting and controls — built alongside your finance
            team, with on-the-ground training so the capability stays in-house.
          </p>
          <div className="badges">
            <div className="badge"><ClipboardCheck size={24} />Practical automation</div>
            <div className="badge"><GraduationCap size={24} />Hands-on team training</div>
            <div className="badge"><ShieldCheck size={24} />Audit-grade governance</div>
            <div className="badge"><LineChart size={24} />Built for regulated finance</div>
          </div>
        </div>
      </>
    ),
  },

  /* NobleOak finance today — our read (proof points, stage, toggleable gaps) */
  {
    id: 'context',
    footnote: 'Figures: NobleOak FY26 results and Appendix 4E, 28 August 2026. Gaps and stage are our outside-in read — click to correct.',
    content: (
      <>
        <div className="head" style={{ marginBottom: 30, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
            <div className="eyebrow">Where NobleOak is today</div>
            <h2 style={{ margin: 0 }}>NobleOak is outgrowing its finance processes</h2>
          </div>
        </div>
        <div className="grid" style={{ gridTemplateColumns: '600px 1fr', gap: 44, alignItems: 'start' }}>
          <div style={{ display: 'grid', gap: 22 }}>
            <div className="proofGrid">
              {[
                ['$549m', 'In-force premiums, +18%'],
                ['$21.1m', 'Underlying NPAT, +15%'],
                ['$1bn', 'In-force target'],
                ['4', 'Partner channels'],
                ['Dec ’27', 'Life Company transition'],
                ['ASX · APRA', 'Listed, regulated'],
              ].map(([n, l]) => (
                <div className="proof" key={l}>
                  <b>{n}</b>
                  <span>{l}</span>
                </div>
              ))}
            </div>
            <FinanceStage />
          </div>
          <div>
            <div className="choiceQ" style={{ marginBottom: 14 }}>
              <span>Pain points we expect</span>
              <span className="choiceHint">Open to see the gaps · click any that don’t apply</span>
            </div>
            <PainThemes />
          </div>
        </div>
      </>
    ),
  },


  /* Who Optiii is */
  {
    id: 'about-optiii',
    content: (
      <>
        <div className="head" style={{ marginBottom: 30 }}>
          <div className="eyebrow">Why Optiii</div>
          <h2 style={{ margin: 0 }}>Optiii is built for exactly this</h2>
        </div>
        <div className="grid" style={{ gridTemplateColumns: '0.9fr 1.1fr', gap: 40, alignItems: 'start', marginBottom: 26 }}>
          <div style={{ display: 'grid', gap: 18 }}>
            <p className="sub" style={{ fontSize: 24, color: '#fff' }}>
              Optiii was founded because of a clear disconnect between AI literacy and what CFOs and finance teams
              actually need.
            </p>
            <p className="sub" style={{ fontSize: 22 }}>
              There’s a lot of hype about AI capability. Finance teams don’t need clever widgets — they need practical
              solutions that stand up to audit, the board and APRA.
            </p>
            <div className="callout" style={{ fontSize: 22, padding: '18px 24px' }}>
              <b>Opinionated, not tool-centric.</b> Every organisation is unique, so we start from your processes and
              systems — not a product we want to sell.
            </div>
          </div>
          <div className="grid g2" style={{ gap: 16 }}>
            {[
              [Layers, 'The technical layer', 'Data, integrations and controls between your systems and your decisions'],
              [GraduationCap, 'Train your team', 'On-the-ground training so your people can execute and own it'],
              [Wrench, 'Build solutions', 'Automations and internal tools, built with your team'],
              [Users, 'Partner & advise', 'Select and implement the right third-party tools, independently'],
            ].map(([Ic, t, d]) => (
              <div className="card" key={t} style={{ padding: 26, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div className="iconWrap" style={{ width: 52, height: 52, borderRadius: 16 }}><Ic size={26} /></div>
                <h3 style={{ margin: 0, fontSize: 24 }}>{t}</h3>
                <p style={{ fontSize: 19 }}>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </>
    ),
  },

  /* 4 — Statement */
  {
    id: 'opportunity',
    variant: 'center',
    hero: true,
    content: (
      <>
        <div className="eyebrow"><Sparkles size={24} />Where finance needs to get to</div>
        <h1 style={{ fontSize: 104, maxWidth: 1600 }}>
          Scale finance to <em>$1bn in-force</em> — without scaling the manual effort.
        </h1>
        <p className="sub" style={{ marginTop: 36, fontSize: 30 }}>
          Less rework, faster close, clean data lineage — and a team trained to run it themselves.
        </p>
        <div className="missionStats">
          {[
            ['BD4', 'Month-end close'],
            ['Hours', 'Board pack, not weeks'],
            ['Traceable', 'Every number to source'],
            ['Owned', 'By NobleOak’s own team'],
          ].map(([n, l]) => (
            <div key={l}>
              <b>{n}</b>
              <span>{l}</span>
            </div>
          ))}
        </div>
      </>
    ),
  },

  /* 5 — Our team (org chart) */
  {
    id: 'team',
    content: (
      <>
        <div className="head" style={{ marginBottom: 28 }}>
          <div className="eyebrow">Why Optiii</div>
          <h2 style={{ margin: 0 }}>We build with your team — and train them</h2>
        </div>
        <div className="org">
          <svg className="orgWires" width="1640" height="640" viewBox="0 0 1640 640" aria-hidden="true">
            <path d="M480 236 V 296" />
            <path d="M960 140 H 1100" />
            <path d="M960 400 H 1100" />
          </svg>

          {/* Optiii delivery team */}
          <div className="orgTeam" style={{ left: 0, top: 0, width: 960, height: 528 }}>
            <span className="orgTeamLabel">Optiii delivery team</span>
          </div>

          <div className="orgCard stack" style={{ left: 28, top: 36, width: 904, height: 200 }}>
            <div style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
              <div className="avatar" style={{ width: 72, height: 72, fontSize: 26 }}>JC</div>
              <div>
                <div className="orgRole">Engagement Director</div>
                <div className="orgName" style={{ margin: 0 }}>{CONTACT.name}</div>
              </div>
            </div>
            <div className="orgFacts">Your day-to-day lead. Runs the diagnostic and owns delivery and training through to handover.</div>
          </div>
          <span className="wireLabel" style={{ left: 496, top: 254 }}>Reports to the Engagement Director</span>

          <div className="orgCard stack" style={{ left: 28, top: 296, width: 904, height: 204 }}>
            <div style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
              <div className="avatar" style={{ width: 72, height: 72 }}><Cpu size={34} /></div>
              <div>
                <div className="orgRole">Assigned to NobleOak</div>
                <div className="orgName" style={{ margin: 0 }}>Automation Analyst</div>
              </div>
            </div>
            <div className="orgFacts">
              Supports, teaches and executes: builds and tests automations with your analysts, writes the playbooks and
              walkthroughs, and runs training sessions.
            </div>
          </div>

          {/* Advisor — secondary to the delivery team */}
          <div className="orgAdvisor" style={{ left: 0, top: 556, width: 960, height: 84 }}>
            <span className="orgRole" style={{ color: 'rgba(245,245,247,0.55)' }}>Advisor</span>
            <b>Dylan C.</b>
            <span>Listed-company CFO · reviews the blueprint and key decisions</span>
          </div>

          {/* NobleOak */}
          <div className="orgClient" style={{ left: 1100, top: 0, width: 540, height: 640 }}>
            <div className="orgTeamLabel" style={{ position: 'static', marginBottom: 4, padding: 0, background: 'none' }}>
              <LogoSlot name="NobleOak" logos={logos} size={22} /> finance team
            </div>
            {[
              ['CFO & sponsor', 'Direction, priorities and sign-off'],
              ['Finance leads', 'Process owners; weekly working sessions'],
              ['Analysts & champions', 'Co-build, then run and extend the workflows'],
            ].map(([t, d]) => (
              <div className="orgClientRow" key={t}>
                <b>{t}</b>
                <span>{d}</span>
              </div>
            ))}
            <div className="orgAsk">
              <span className="dayLabel">From NobleOak</span>
              A sponsor, a few hours a week from finance leads, and read-only system access.
            </div>
          </div>
          <span className="wireLabel" style={{ left: 972, top: 112 }}>Day-to-day</span>
          <span className="wireLabel" style={{ left: 972, top: 372 }}>Support &amp; training</span>
        </div>
      </>
    ),
  },

  /* Agenda */
  {
    id: 'agenda',
    content: (
      <>
        <div className="head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <h2 style={{ margin: 0 }}>Agenda</h2>
          <span className="badge"><Clock size={20} />30 minutes</span>
        </div>
        <div className="agenda">
          {AGENDA.map(([t, d, mins], i) => (
            <div key={t} className={`agendaItem ${i === AGENDA.length - 1 ? 'discussion' : ''}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div className="agendaNum">{i + 1}</div>
                <span className="agendaTime">{mins} min</span>
              </div>
              <h3>{t}</h3>
              <p>{d}</p>
            </div>
          ))}
          <div className="agendaItem readAfter">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div className="agendaNum"><BookOpen size={48} /></div>
              <span className="agendaTime">After the meeting</span>
            </div>
            <h3>Appendix</h3>
            <p>Track record, training, the diagnostic day by day, your systems and data, and specialist tools.</p>
          </div>
        </div>
      </>
    ),
  },

  /* 8 — Your accounting system → how we connect it */
  {
    id: 'ledger',
    variant: 'alt',
    content: (
      <>
        <div className="head" style={{ marginBottom: 30, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
            <div className="eyebrow">Your finance stack</div>
            <h2 style={{ margin: 0 }}>Connecting your systems</h2>
          </div>
          <Prompt>Pick your accounting system to see how we’d connect it</Prompt>
        </div>
        <FocusChoice id="ledger" logos={logos} prompt="Select one" actions={<Segmented id="approach" label="Your choice" />}>
          {(ledger) => <LedgerFlow ledger={ledger} />}
        </FocusChoice>
      </>
    ),
  },

  /* Where NobleOak's numbers come from (live data map) */
  {
    id: 'stack',
    variant: 'alt',
    content: (
      <>
        <div className="head" style={{ marginBottom: 30 }}>
          <div className="eyebrow">Your finance stack</div>
          <h2 style={{ margin: 0 }}>Where NobleOak’s numbers come from</h2>
        </div>
        <div className="grid" style={{ gridTemplateColumns: '0.95fr 1.05fr', gap: 44, alignItems: 'start' }}>
          <div style={{ display: 'grid', gap: 30 }}>
            <Choice id="policyData" compact />
            <Choice id="reporting" logos={logos} compact />
            <Choice id="teamSize" compact />
          </div>
          <DataMap />
        </div>
        <Advice style={{ marginTop: 26 }}>
          Spreadsheets aren’t the problem; <b>unowned spreadsheets are.</b> Before automating any recurring workbook, give
          it a named owner and agree which system is its source of truth.
        </Advice>
      </>
    ),
  },

  /* 10b — Workplace & AI */
  {
    id: 'workplace-ai',
    content: (
      <>
        <div className="head" style={{ marginBottom: 40, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
            <div className="eyebrow">Workplace &amp; AI</div>
            <h2 style={{ margin: 0 }}>We work inside your environment</h2>
          </div>
          <Prompt>Click to answer</Prompt>
        </div>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 56, alignItems: 'start' }}>
          <FocusChoice id="workspace" logos={logos} columns={2} prompt="Select one">
            {(ws) => (
              <div className="soft" style={{ padding: 36 }}>
                <div className="checks" style={{ gap: 16 }}>
                  {WORKSPACE_NOTES[ws].map((t) => <Check key={t}>{t}</Check>)}
                </div>
              </div>
            )}
          </FocusChoice>
          <div style={{ display: 'grid', gap: 28 }}>
            <Choice id="aiTools" logos={logos} compact />
            <Advice>
              <b>Pick one approved tool for finance and write the rules down</b> before people experiment. We only use
              tools you’ve approved; personal data is removed before anything reaches a model, and your team reviews
              every output.
            </Advice>
          </div>
        </div>
      </>
    ),
  },

  /* 11 — Close deadlines */
  {
    id: 'close',
    footnote: 'NobleOak’s ASX results dates: FY26 on 28 Aug 2026, 1H FY26 on 27 Feb 2026. Board update 2 weeks and audit sign-off 3 weeks before release.',
    content: (
      <>
        <div className="head" style={{ marginBottom: 30, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
            <div className="eyebrow">NobleOak’s close &amp; reporting calendar</div>
            <h2 style={{ margin: 0 }}>How much room NobleOak’s close leaves</h2>
          </div>
        </div>
        <ClosePeriodProvider>
          <FocusChoice id="closeDay" columns={4} prompt="Select one" actions={<CloseActions />}>
            {(choice) => <CloseTimeline choice={choice} />}
          </FocusChoice>
        </ClosePeriodProvider>
      </>
    ),
  },

  /* Engagement model — timeline */
  {
    id: 'engagement-model',
    variant: 'alt',
    content: (
      <>
        <div className="head" style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
            <div className="eyebrow">How we’d work together</div>
            <h2 style={{ margin: 0 }}>From diagnostic to live in 3.5 months</h2>
          </div>
        </div>
        <EngagementTimeline />
      </>
    ),
  },

  /* 11 — On the ground with your team (questions) */
  {
    id: 'training',
    content: (
      <>
        <div className="head" style={{ marginBottom: 40 }}>
          <div className="eyebrow"><GraduationCap size={24} />On-the-ground training</div>
          <h2>We work inside your finance team, not beside it</h2>
          <p className="sub" style={{ fontSize: 24, maxWidth: 1500 }}>
            Automation only sticks when the people running close own it. We build with your team on live work, train as
            we go, and leave capability behind — not a black box.
          </p>
        </div>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 56 }}>
          <div className="checks" style={{ gap: 22, alignContent: 'start' }}>
            {[
              [MapPin, 'Alongside real month-ends', 'We support the team through live closes, so changes fit how work actually happens.'],
              [Users, 'Build with, not for', 'Your analysts co-build each workflow, so they can run, fix and extend it.'],
              [UserCheck, 'Internal champions', 'We train two or three champions to own the workflows and coach others.'],
              [BookOpen, 'Playbooks and walkthroughs', 'Every workflow documented, with a short recorded walkthrough.'],
              [LifeBuoy, 'Support through go-live', 'We stay through the first live month-ends after each release.'],
            ].map(([Ic, t, d]) => (
              <div key={t} style={{ display: 'flex', gap: 22, alignItems: 'flex-start' }}>
                <div className="iconWrap" style={{ width: 60, height: 60, borderRadius: 18 }}><Ic size={28} /></div>
                <div>
                  <div style={{ fontSize: 25, fontWeight: 600 }}>{t}</div>
                  <div style={{ fontSize: 21, color: 'rgba(245,245,247,0.72)', lineHeight: 1.5 }}>{d}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="soft" style={{ display: 'grid', gap: 36, alignContent: 'start' }}>
            <Choice id="training" compact />
            <Choice id="location" compact />
          </div>
        </div>
      </>
    ),
  },

  /* 12 — Two-week diagnostic, day by day */
  {
    id: 'diagnostic-plan',
    variant: 'alt',
    content: (
      <>
        <div className="head" style={{ marginBottom: 44 }}>
          <div className="eyebrow">Proposed first step</div>
          <h2>Two weeks alongside your team, day by day</h2>
          <p className="sub" style={{ fontSize: 24, maxWidth: 1500 }}>
            Most of the fortnight is spent alongside your team — watching the real work, not reading process documents.
          </p>
        </div>
        <div className="days">
          {[
            ['Days 1–2', 'Kick-off & interviews', 'Align goals with the CFO, interview finance leads, agree scope and system access.', 'CFO, finance leads'],
            ['Days 3–4', 'Shadow the close', 'Sit with the team through close and reporting tasks; map steps, handoffs and controls.', 'Finance team'],
            ['Days 5–6', 'Systems & data', 'Review the ledger, policy data, partner feeds and reporting tools; trace key numbers to source.', 'Finance systems, IT'],
            ['Days 7–8', 'Size & prioritise', 'Quantify effort, risk and value; test the opportunity register with the team in a working session.', 'Finance leads'],
            ['Days 9–10', 'Blueprint & readout', 'Roadmap, training plan and implementation proposal, presented to the CFO and leadership.', 'CFO, leadership'],
          ].map(([d, t, p, who]) => (
            <div className="day" key={d}>
              <div className="dayLabel">{d}</div>
              <h3>{t}</h3>
              <p>{p}</p>
              <div className="who"><Users size={18} />{who}</div>
            </div>
          ))}
        </div>
        <div className="baseline">
          <span className="focusLabel">What we baseline in the diagnostic</span>
          {['Close cycle time', 'Reconciliation exceptions', 'Forecast accuracy', 'Board pack hours', 'Key-person risk'].map((m) => (
            <span className="badge" key={m}><Gauge size={18} />{m}</span>
          ))}
        </div>
        <Advice style={{ marginTop: 22 }}>
          <b>Book finance leads’ time before kick-off.</b> The biggest risk to a two-week diagnostic is calendars, not
          systems — especially in the weeks around a close.
        </Advice>
      </>
    ),
  },

  /* 13 — Investment */
  {
    id: 'investment',
    content: (
      <div className="grid" style={{ gridTemplateColumns: '1.05fr 0.95fr', gap: 56, alignItems: 'start' }}>
        <div>
          <div className="eyebrow">The first step</div>
          <h2>Diagnostic: $22,500</h2>
          <div className="grid g2" style={{ margin: '30px 0 36px' }}>
            <div className="card"><div className="eyebrow" style={{ marginBottom: 12, fontSize: 18 }}>Duration</div><div className="resultNum" style={{ fontSize: 60, margin: 0 }}>2 weeks</div></div>
            <div className="card"><div className="eyebrow" style={{ marginBottom: 12, fontSize: 18 }}>Fee</div><div className="resultNum" style={{ fontSize: 60, margin: 0 }}>$22,500</div><div className="resultDetail">AUD, excl. GST</div></div>
          </div>
          <h3 style={{ marginBottom: 20 }}>Included</h3>
          <div className="checks" style={{ gap: 14 }}>
            <Check>Current-state map of close, reporting, data lineage and controls</Check>
            <Check>Sized opportunity register — quick wins and structural fixes</Check>
            <Check>Roadmap and training plan, sequenced around the Life Company transition</Check>
            <Check>Readout to the CFO and a fixed proposal for implementation</Check>
          </div>
          <p className="note" style={{ marginTop: 22, fontSize: 21, color: '#C4B5FD' }}>
            Covers the diagnostic only — implementation is quoted at the readout. Do it with us or yourselves.
          </p>
        </div>
        <div className="soft raised" style={{ display: 'grid', gap: 30 }}>
          <div>
            <h3 style={{ marginBottom: 18 }}>What we need from NobleOak</h3>
            <div className="checks" style={{ gap: 12 }}>
              <Check>A sponsor in the CFO’s office, about 3 hours across the fortnight</Check>
              <Check>Time with finance leads, about a day each</Check>
              <Check>Read-only access to the ledger and reporting tools</Check>
              <Check>A named contact in finance for day-to-day questions</Check>
            </div>
          </div>
          <Choice id="start" compact columns={2} />
        </div>
      </div>
    ),
  },

  /* 16 — Results (from optiii.com) */
  {
    id: 'results',
    variant: 'alt',
    footnote: 'Representative of engagements where automation was implemented with clear ownership, governance and executive sponsorship.',
    content: (
      <>
        <div className="head" style={{ marginBottom: 30 }}>
          <div className="eyebrow">Track record</div>
          <h2 style={{ margin: 0 }}>We’ve taken close from Day 16 to Day 4</h2>
        </div>
        <div className="grid" style={{ gridTemplateColumns: '1.35fr 0.65fr', gap: 32, alignItems: 'stretch' }}>
          <div style={{ display: 'grid', gap: 18 }}>
            <div className="card resultHero">
              <div className="resultHeroTop">
                <div>
                  <div className="dayLabel">Month-end close</div>
                  <div className="resultBig">75% faster</div>
                </div>
                <div className="resultHeroSub">Business days after month-end until the books are closed</div>
              </div>
              <div className="closeTrack">
                <div className="ctRow">
                  <span className="ctLabel">Before</span>
                  <div className="ctBar"><div className="ctFill before" style={{ width: '100%' }}><b>Day 16</b></div></div>
                </div>
                <div className="ctRow">
                  <span className="ctLabel">After</span>
                  <div className="ctBar"><div className="ctFill after" style={{ width: `${(4 / 16) * 100}%` }}><b>Day 4</b></div>
                    <span className="ctGain">12 business days back every month</span>
                  </div>
                </div>
                <div className="ctScale">
                  {Array.from({ length: 16 }, (_, k) => (
                    <span key={k} className={k + 1 === 4 || k + 1 === 16 ? 'on' : ''}>{k + 1}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="grid g3" style={{ gap: 18 }}>
              {[
                ['Board pack', '1–2 weeks', '1–2 hours'],
                ['Reporting cadence', 'Quarterly', 'Near real-time'],
                ['Finance effort', 'Spreadsheet-driven', 'Exception-based'],
              ].map(([label, from, to]) => (
                <div className="card baCard" key={label}>
                  <div className="dayLabel">{label}</div>
                  <div className="baFrom">{from}</div>
                  <div className="baTo"><ArrowRight size={22} />{to}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="soft raised" style={{ padding: 32, display: 'flex', flexDirection: 'column', gap: 18 }}>
            <h3 style={{ margin: 0 }}>What drives it</h3>
            <div className="checks" style={{ gap: 14 }}>
              <Check>Fixed cut-offs and a soft close</Check>
              <Check>Daily reconciliations, not month-end</Check>
              <Check>Data flowing automatically from source systems</Check>
              <Check>Exception-only review, with named owners</Check>
            </div>
            <div className="resultLink">
              <span className="dayLabel">For NobleOak</span>
              We’ll show what a BD4 close would mean for NobleOak’s own audit, board and results calendar.
            </div>
          </div>
        </div>
      </>
    ),
  },

  /* 17 — Your answers */
  {
    id: 'responses',
    content: (
      <>
        <div className="head" style={{ marginBottom: 32 }}>
          <div className="eyebrow">Your answers</div>
          <h2 style={{ margin: 0 }}>What we heard today</h2>
          <p className="sub" style={{ marginTop: 14, fontSize: 24 }}>Captured as we went — we’ll use these to tailor the diagnostic.</p>
        </div>
        <ResponsesSummary />
      </>
    ),
  },

  /* Next steps — personalised plan */
  {
    id: 'next-steps',
    variant: 'center',
    hero: true,
    footnote: '© Optiii · Based in Australia • Serving teams globally',
    content: (
      <div className="grid" style={{ gridTemplateColumns: '1.15fr 0.85fr', gap: 56, alignItems: 'center' }}>
        <div>
          <div className="eyebrow"><Sparkles size={24} />Next steps</div>
          <h1 style={{ fontSize: 76, marginBottom: 30 }}>Let’s get the diagnostic started</h1>
          <YourPlan />
          <div style={{ display: 'flex', gap: 16, marginTop: 34 }}>
            <div className="btn">Approve the two-week diagnostic · $22,500 <ArrowRight size={24} /></div>
          </div>
          <div className="scopeNote">
            <Info size={22} style={{ flex: 'none', marginTop: 3, color: '#C4B5FD' }} />
            <span>
              <b>$22,500 (excl. GST) covers the two-week diagnostic only.</b> Implementation, training and any retainer
              are quoted as a fixed proposal at the readout — with no obligation to go ahead.
            </span>
          </div>
        </div>
        <div className="soft raised">
          <div style={{ marginBottom: 24 }}><Brand size={30} /></div>
          <div style={{ display: 'flex', gap: 24, alignItems: 'center', marginBottom: 28 }}>
            <div className="avatar" style={{ width: 80, height: 80, fontSize: 28 }}>JC</div>
            <div>
              <div style={{ fontSize: 30, fontWeight: 600 }}>{CONTACT.name}</div>
              <div className="role" style={{ marginTop: 2 }}>{CONTACT.title}</div>
            </div>
          </div>
          <div className="checks" style={{ gap: 14, marginBottom: 28 }}>
            <div className="check"><Mail size={24} /><span>{CONTACT.email}</span></div>
          </div>
          <p className="note" style={{ fontSize: 16, color: 'rgba(245,245,247,0.52)' }}>
            Optiii provides management consulting and implementation services only. Optiii does not provide financial
            product advice, deal in financial products, or hold an Australian Financial Services Licence (AFSL).
          </p>
        </div>
      </div>
    ),
  },
];

/* Running order: the meeting slides first, then the appendix (skipped in meeting mode) */
const MEETING = [
  ['cover'],
  ['agenda'],
  ['context', 'Where you are'],
  ['opportunity', 'Where you are'],
  ['why-finance-hard', 'Getting it right'],
  ['advice-build-buy', 'Getting it right'],
  ['advice-apra-ai', 'Getting it right'],
  ['advice-life-co', 'The deadline'],
  ['about-optiii', 'Why Optiii'],
  ['team', 'Why Optiii'],
  ['engagement-model', 'How we’d work together'],
  ['investment', 'First step'],
  ['next-steps'],
];

/* [id, footer section, label in the appendix contents] */
const APPENDIX = [
  ['results', 'Why Optiii', 'Track record: Day 16 to Day 4'],
  ['training', 'How we’d work together', 'How we train your team'],
  ['diagnostic-plan', 'First step', 'The diagnostic, day by day'],
  ['close', 'Your systems & calendar', 'NobleOak’s close and results calendar'],
  ['ledger', 'Your systems & calendar', 'Connecting your systems'],
  ['advice-data', 'Your systems & calendar', 'Data and security'],
  ['stack', 'Your systems & calendar', 'Where NobleOak’s numbers come from'],
  ['advice-fast-tools', 'Getting it right', 'Specialist tools worth buying'],
  ['workplace-ai', 'Getting it right', 'Workplace and approved AI tools'],
  ['project-cadence', 'How we’d work together', 'Project cadence'],
  ['responses', 'First step', 'What we heard today'],
];

const APPENDIX_START = MEETING.length + 2; // page number of the first appendix slide

const appendixDivider = {
  id: 'appendix',
  variant: 'alt',
  content: (
    <div className="grid" style={{ gridTemplateColumns: '0.8fr 1.2fr', gap: 72, alignItems: 'center' }}>
      <div>
        <div className="eyebrow"><BookOpen size={24} />Appendix</div>
        <h2>For reading after the meeting</h2>
        <p className="sub" style={{ fontSize: 24 }}>
          The detail behind today’s conversation — your calendar, systems, data and AI tools.
          The questions on these pages save as you click.
        </p>
      </div>
      <div className="appendixList">
        {APPENDIX.map(([id, section, label], i) => (
          <div className="appendixRow" key={id}>
            <span className="appendixNum">{String(APPENDIX_START + i).padStart(2, '0')}</span>
            <span className="appendixLabel">{label}</span>
            <span className="appendixSection">{section}</span>
          </div>
        ))}
      </div>
    </div>
  ),
};

const all = [...slides, ...adviceSlides, appendixDivider];
const find = (id) => {
  const slide = all.find((x) => x.id === id);
  if (!slide) throw new Error(`Missing slide: ${id}`);
  return slide;
};
const ordered = [
  ...MEETING.map(([id, section]) => ({ ...find(id), ...(section ? { section } : {}) })),
  { ...find('appendix'), section: 'Appendix', postRead: true },
  ...APPENDIX.map(([id, section]) => ({ ...find(id), section: `Appendix · ${section}`, postRead: true })),
];

export default ordered;
